import { PageHero, SectionHeader } from "@/components/Section";
import useReveal from "@/hooks/useReveal";
import { church, fullAddress } from "@/config/church";
import { Mail, Phone, MapPin, ArrowRight, Facebook, Instagram, Youtube } from "lucide-react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";

export default function Contact() {
  useReveal();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [prayer, setPrayer] = useState({ name: "", email: "", request: "", is_public: false, is_urgent: false });
  const [busy, setBusy] = useState(false);
  const [busyPrayer, setBusyPrayer] = useState(false);

  const submitContact = async (e) => {
    e.preventDefault();
    try {
      setBusy(true);
      await api.post("/contact", form);
      setForm({ name: "", email: "", phone: "", subject: "", message: "" });
      navigate("/thank-you?source=contact");
    } catch {
      toast.error("Could not send. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const submitPrayer = async (e) => {
    e.preventDefault();
    try {
      setBusyPrayer(true);
      await api.post("/prayer", prayer);
      setPrayer({ name: "", email: "", request: "", is_public: false, is_urgent: false });
      navigate("/thank-you?source=prayer");
    } catch {
      toast.error("Could not submit. Please try again.");
    } finally {
      setBusyPrayer(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <PageHero
        eyebrow="Contact"
        title="We'd love to hear from you."
        subtitle="Have a question, need prayer, or want to connect? Reach out below and one of our team members will follow up personally."
        image="https://images.pexels.com/photos/7220101/pexels-photo-7220101.jpeg?auto=compress&cs=tinysrgb&w=2000"
      />

      <section className="bg-white py-24">
        <div className="container-npbc grid gap-6 md:grid-cols-3 mb-16">
          {[
            { icon: MapPin, label: "Address", value: fullAddress, href: `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(fullAddress)}` },
            { icon: Phone, label: "Phone", value: church.phone, href: `tel:${church.phoneRaw}` },
            { icon: Mail, label: "Email", value: church.email, href: `mailto:${church.email}` },
          ].map((c, i) => (
            <a
              key={c.label}
              href={c.href || undefined}
              target={c.href ? "_blank" : undefined}
              rel="noreferrer noopener"
              data-reveal
              style={{ transitionDelay: `${i * 60}ms` }}
              className="card-elevated p-6 block group hover:-translate-y-1 transition-all duration-500"
            >
              <div className="h-11 w-11 rounded-2xl bg-npbc-red/10 text-npbc-red grid place-items-center group-hover:bg-npbc-red group-hover:text-white transition">
                <c.icon className="h-5 w-5" />
              </div>
              <div className="mt-5 text-[10px] font-semibold uppercase tracking-[0.24em] text-npbc-gray/70">{c.label}</div>
              <div className="mt-1 heading-serif text-lg leading-snug">{c.value}</div>
            </a>
          ))}
        </div>

        <div className="container-npbc grid lg:grid-cols-2 gap-8">
          {/* Contact Form */}
          <form onSubmit={submitContact} data-reveal className="card-elevated p-8 md:p-10 space-y-4">
            <div>
              <div className="eyebrow">General Contact</div>
              <h2 className="mt-3 heading-serif text-4xl leading-tight">Send us a message.</h2>
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <input required placeholder="Your name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="contact-form-name" className="rounded-xl border border-black/10 px-4 py-3 text-sm outline-none focus:border-npbc-red focus:ring-2 focus:ring-npbc-red/20" />
              <input required type="email" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="contact-form-email" className="rounded-xl border border-black/10 px-4 py-3 text-sm outline-none focus:border-npbc-red focus:ring-2 focus:ring-npbc-red/20" />
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <input placeholder="Phone (optional)" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="contact-form-phone" className="rounded-xl border border-black/10 px-4 py-3 text-sm outline-none focus:border-npbc-red focus:ring-2 focus:ring-npbc-red/20" />
              <input placeholder="Subject" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} data-testid="contact-form-subject" className="rounded-xl border border-black/10 px-4 py-3 text-sm outline-none focus:border-npbc-red focus:ring-2 focus:ring-npbc-red/20" />
            </div>
            <textarea required rows={6} placeholder="How can we serve you?" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} data-testid="contact-form-message" className="w-full rounded-xl border border-black/10 px-4 py-3 text-sm outline-none focus:border-npbc-red focus:ring-2 focus:ring-npbc-red/20" />
            <button type="submit" disabled={busy} data-testid="contact-form-submit" className="btn-primary w-full justify-center">
              {busy ? "Sending..." : "Send Message"} <ArrowRight className="h-4 w-4" />
            </button>
          </form>

          {/* Prayer Request Form */}
          <form onSubmit={submitPrayer} data-reveal className="card-elevated p-8 md:p-10 space-y-4 bg-npbc-black text-white">
            <div>
              <div className="eyebrow !text-npbc-gold">Prayer Request</div>
              <h2 className="mt-3 heading-serif text-4xl leading-tight">Let our team pray for you.</h2>
              <p className="mt-2 text-white/70 text-sm">Every request is prayed over. You can choose to keep it private or share on our public Prayer Wall.</p>
            </div>
            <input required placeholder="Your name" value={prayer.name} onChange={(e) => setPrayer({ ...prayer, name: e.target.value })} data-testid="prayer-form-name" className="w-full rounded-xl border border-white/15 bg-white/[0.05] px-4 py-3 text-sm outline-none placeholder:text-white/50 focus:border-npbc-gold" />
            <input type="email" placeholder="Email (optional)" value={prayer.email} onChange={(e) => setPrayer({ ...prayer, email: e.target.value })} data-testid="prayer-form-email" className="w-full rounded-xl border border-white/15 bg-white/[0.05] px-4 py-3 text-sm outline-none placeholder:text-white/50 focus:border-npbc-gold" />
            <textarea required rows={5} placeholder="What would you like us to pray about?" value={prayer.request} onChange={(e) => setPrayer({ ...prayer, request: e.target.value })} data-testid="prayer-form-request" className="w-full rounded-xl border border-white/15 bg-white/[0.05] px-4 py-3 text-sm outline-none placeholder:text-white/50 focus:border-npbc-gold" />
            <div className="flex flex-wrap items-center gap-5 text-sm">
              <label className="inline-flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={prayer.is_public} onChange={(e) => setPrayer({ ...prayer, is_public: e.target.checked })} className="h-4 w-4 accent-npbc-gold" />
                Share on Prayer Wall
              </label>
              <label className="inline-flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={prayer.is_urgent} onChange={(e) => setPrayer({ ...prayer, is_urgent: e.target.checked })} className="h-4 w-4 accent-npbc-red" />
                Urgent
              </label>
            </div>
            <button type="submit" disabled={busyPrayer} data-testid="prayer-form-submit" className="btn-gold w-full justify-center">
              {busyPrayer ? "Sending..." : "Submit Prayer Request"} <ArrowRight className="h-4 w-4" />
            </button>
          </form>
        </div>

        {/* Socials */}
        <div className="container-npbc mt-16 text-center" data-reveal>
          <div className="eyebrow justify-center flex">Follow the Journey</div>
          <h3 className="mt-3 heading-serif text-3xl">Stay connected on social.</h3>
          <div className="mt-6 flex items-center justify-center gap-3">
            {[
              { href: church.social.facebook, Icon: Facebook, label: "Facebook" },
              { href: church.social.instagram, Icon: Instagram, label: "Instagram" },
              { href: church.social.youtube, Icon: Youtube, label: "YouTube" },
            ].map(({ href, Icon, label }) => (
              <a key={label} href={href} target="_blank" rel="noreferrer noopener" aria-label={label} className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-black/10 hover:border-npbc-red hover:text-npbc-red transition">
                <Icon className="h-4 w-4" />
              </a>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
